/**
 * API error taxonomy + retry policy (§7 of the resilience spec).
 *
 * Every rejection leaving the axios interceptor carries {kind, status?,
 * retryAfterMs?} so that React Query's retry predicate — and any UI error
 * copy — can tell "server is down, retry with backoff" apart from "your
 * session died, do NOT retry, go log in".
 *
 * Retryable (transient): offline, timeout (incl. HTTP 408), rate_limited
 * (429, honors Retry-After), server (5xx), network failures.
 * Never retried: auth (401 — the refresh flow owns that), forbidden (403),
 * not_found (404), bad_request (other 4xx), unknown shapes.
 */

export type ApiErrorKind =
  | 'offline'
  | 'timeout'
  | 'rate_limited'
  | 'server'
  | 'auth'
  | 'forbidden'
  | 'not_found'
  | 'bad_request'
  | 'unknown';

export interface ClassifiedApiError extends Error {
  kind: ApiErrorKind;
  status?: number;
  retryAfterMs?: number;
}

export const MAX_QUERY_RETRIES = 3;
export const RETRY_BASE_MS = 1000;
export const RETRY_CAP_MS = 10_000;
export const RETRY_AFTER_CAP_MS = 30_000;

export function parseRetryAfterMs(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value * 1000;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (/^\d+$/.test(s)) return parseInt(s, 10) * 1000;
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return Math.max(0, t - Date.now());
  }
  return undefined;
}

function isOfflineSignal(error: {
  code?: string;
  message?: string;
  response?: unknown;
}): boolean {
  if (error.response) return false;
  if (error.code === 'ERR_NETWORK') return true;
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('network error') || msg.includes('failed to fetch') || msg.includes('load failed')) {
    return true;
  }
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function isTimeoutSignal(error: { code?: string; message?: string }): boolean {
  if (error.code === 'ECONNABORTED') return true;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('timeout') && msg.includes('exceeded');
}

export function classifyApiError(error: unknown): ClassifiedApiError {
  const e = (error ?? {}) as {
    code?: string;
    message?: string;
    response?: { status?: number; data?: { error?: string }; headers?: Record<string, unknown> };
  };
  const message =
    e.response?.data?.error || e.message || 'An unexpected error occurred';
  const out = new Error(message) as ClassifiedApiError;
  out.status = typeof e.response?.status === 'number' ? e.response.status : undefined;
  out.retryAfterMs = parseRetryAfterMs(e.response?.headers?.['retry-after']);

  if (isOfflineSignal(e)) {
    out.kind = 'offline';
    return out;
  }
  const status = out.status;
  if (status === 401) {
    out.kind = 'auth';
  } else if (status === 403) {
    out.kind = 'forbidden';
  } else if (status === 404) {
    out.kind = 'not_found';
  } else if (status === 429) {
    out.kind = 'rate_limited';
  } else if (status === 408 || isTimeoutSignal(e)) {
    out.kind = 'timeout';
  } else if (status != null && status >= 500 && status <= 599) {
    out.kind = 'server';
  } else if (status != null && status >= 400 && status <= 499) {
    out.kind = 'bad_request';
  } else {
    out.kind = 'unknown';
  }
  return out;
}

export function isTransientKind(kind: ApiErrorKind): boolean {
  return kind === 'offline' || kind === 'timeout' || kind === 'rate_limited' || kind === 'server';
}

/** React Query `retry` predicate: transient kinds only, bounded attempts. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  const kind =
    error instanceof Error && 'kind' in error
      ? (error as Partial<ClassifiedApiError>).kind
      : classifyApiError(error).kind;
  return kind != null && isTransientKind(kind);
}

/** React Query `retryDelay`: exponential backoff, capped, honors Retry-After. */
export function queryRetryDelay(attemptIndex: number, error?: unknown): number {
  const backoff = Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attemptIndex), RETRY_CAP_MS);
  const retryAfter =
    error instanceof Error && 'retryAfterMs' in error
      ? (error as Partial<ClassifiedApiError>).retryAfterMs
      : undefined;
  if (typeof retryAfter === 'number' && retryAfter >= 0) {
    return Math.min(Math.max(backoff, retryAfter), RETRY_AFTER_CAP_MS);
  }
  return backoff;
}

/** Human-readable copy per kind — pages show this instead of raw messages. */
export function errorCopyFor(kind: ApiErrorKind): string {
  switch (kind) {
    case 'offline':
      return 'You appear to be offline. Showing the last loaded data — retry when reconnected.';
    case 'timeout':
      return 'The server is taking too long to respond. Retry in a moment.';
    case 'rate_limited':
      return 'Too many requests — backing off. Retry shortly.';
    case 'server':
      return 'Unable to connect to server. Your work is safe — retry shortly.';
    case 'auth':
      return 'Your session expired. Please sign in again.';
    case 'forbidden':
      return 'Permission denied for this action.';
    case 'not_found':
      return 'The requested item was not found. It may have been removed.';
    case 'bad_request':
      return 'The request was invalid and was not retried.';
    default:
      return 'Something went wrong. Retry, and contact support if it persists.';
  }
}
